import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Box,
  Button,
  LinearProgress,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "common/locales";
import { AnalyticsEvents, analytics } from "../../common/analytics";

import RuumiLogo from "assets/images/ruumi-logo-light.png";
import LoginCarousel from "../../app/LoginCarousel";

const DARK_GREEN = "#1C4321";
const LIGHT_GREEN = "#B5E48C";
const LINK_COLOR = "#F7FCF1";

const PageContainer = styled(Box)({
  display: "flex",
  minHeight: "100vh",
  width: "100%",
});

const CarouselSection = styled(Box)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("sm")]: {
    display: "block",
    flex: "0 0 60%",
    height: "100vh",
    position: "relative",
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: DARK_GREEN,
  padding: theme.spacing(4),
  minHeight: "100vh",
  position: "relative",
  [theme.breakpoints.down("sm")]: {
    paddingTop: `calc(env(safe-area-inset-top) + ${theme.spacing(4)})`,
    paddingBottom: `calc(env(safe-area-inset-bottom) + ${theme.spacing(4)})`,
  },
}));

const FormContainer = styled(Box)({
  width: "100%",
  maxWidth: "360px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const Logo = styled("img")({
  width: "120px",
  height: "auto",
  marginBottom: "32px",
});

const StyledTextField = styled(TextField)({
  width: "100%",
  marginBottom: "16px",

  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "12px",

    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: LIGHT_GREEN,
    },

    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: LIGHT_GREEN,
      borderWidth: "2px",
    },
  },

  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
  },

  "& .MuiInputLabel-root": {
    color: "#666",

    "&.Mui-focused": {
      color: DARK_GREEN,
    },

    "&.MuiInputLabel-shrink": {
      backgroundColor: "white",
      padding: "0 6px",
      marginLeft: "-4px",
      borderRadius: "12px 12px 0 0",
      border: `2px solid ${LIGHT_GREEN}`,
      borderBottom: "2px solid white",
      transform: "translate(14px, -18px) scale(0.75)",
    },
  },

  "& .MuiOutlinedInput-input": {
    padding: "16px",
  },
});

const SubmitButton = styled(Button)({
  width: "100%",
  backgroundColor: LIGHT_GREEN,
  color: DARK_GREEN,
  borderRadius: "12px",
  padding: "14px 24px",
  fontSize: "1.1rem",
  fontWeight: 600,
  textTransform: "none",
  marginTop: "8px",
  border: `2px solid ${LIGHT_GREEN}`,
  transition: "all 0.2s ease-in-out",

  "&:hover": {
    backgroundColor: DARK_GREEN,
    color: LIGHT_GREEN,
    borderColor: LIGHT_GREEN,
  },

  "&:disabled": {
    backgroundColor: "rgba(181, 228, 140, 0.5)",
    color: "rgba(28, 67, 33, 0.5)",
    borderColor: "transparent",
  },

  "& .MuiButton-endIcon": {
    marginLeft: "8px",
    color: "inherit",
  },

  "& .MuiSvgIcon-root": {
    color: "inherit",
  },
});

const LinksContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  width: "100%",
  marginTop: "24px",
});

const LinkButton = styled(Button)({
  color: LINK_COLOR,
  textTransform: "none",
  fontWeight: 400,
  padding: "8px 0",

  "&:hover": {
    backgroundColor: "transparent",
    textDecoration: "underline",
  },
});

const ProgressBar = styled(LinearProgress)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,

  "& .MuiLinearProgress-bar": {
    backgroundColor: LIGHT_GREEN,
  },

  "&.MuiLinearProgress-root": {
    backgroundColor: "rgba(181, 228, 140, 0.3)",
  },
});

const ErrorText = styled(Typography)({
  color: "#ff6b6b",
  textAlign: "center",
  marginBottom: "16px",
  fontSize: "0.9rem",
});

const LegalText = styled(Typography)({
  color: LINK_COLOR,
  textAlign: "center",
  fontSize: "0.75rem",
  marginTop: "16px",
  lineHeight: 1.4,

  "& a": {
    color: LIGHT_GREEN,
    textDecoration: "underline",

    "&:hover": {
      textDecoration: "none",
    },
  },
});

const SignupIntroDialog = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const storedEmail = window.localStorage.getItem("signupEmail");
    if (storedEmail) {
      setEmail(storedEmail);
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    analytics.track(AnalyticsEvents.SIGNUP_INTRO_SUBMITTED);

    try {
      window.localStorage.setItem("signupEmail", email);
      window.localStorage.setItem("signupName", name);
      window.localStorage.setItem("signupPhone", phone);

      navigate("/signup/user-details");
    } catch (err) {
      console.error("Signup intro error:", err);
      window.localStorage.setItem("signupEmail", email);
      window.localStorage.setItem("signupName", name);
      window.localStorage.setItem("signupPhone", phone);
      navigate("/signup/user-details");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  return (
    <PageContainer>
      {!isMobile && (
        <CarouselSection>
          <LoginCarousel />
        </CarouselSection>
      )}
      <FormSection>
        {loading && <ProgressBar />}
        <FormContainer>
          <Logo src={RuumiLogo} alt="Ruumi logo" />

          {error && <ErrorText>{error}</ErrorText>}

          <form onSubmit={handleSubmit} style={{ width: "100%" }}>
            <StyledTextField
              variant="outlined"
              required
              id="input-name"
              disabled={loading}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              label={t("signup.name.label")}
              autoFocus={true}
              data-private
            />
            <StyledTextField
              variant="outlined"
              required
              id="input-email"
              disabled={loading}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              label={t("signup.mail.label")}
              data-private
            />
            <StyledTextField
              variant="outlined"
              required
              id="input-phone"
              disabled={loading}
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              label={t("signup.phone.label")}
              data-private
            />
            <SubmitButton
              type="submit"
              variant="contained"
              disabled={
                loading || !name.trim() || !email.trim() || !phone.trim()
              }
              endIcon={<ArrowForwardIcon />}
              disableElevation
            >
              Continue
            </SubmitButton>
          </form>

          <LegalText>
            By continuing, you agree to our{" "}
            <a
              href="https://www.ruumi.io/data-protection-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Data Protection Policy
            </a>{" "}
            and{" "}
            <a
              href="https://www.ruumi.io/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms and Conditions
            </a>
          </LegalText>

          <LinksContainer>
            <LinkButton onClick={handleBackToLogin}>
              Already have an account? Sign in
            </LinkButton>
          </LinksContainer>
        </FormContainer>
      </FormSection>
    </PageContainer>
  );
};

export default SignupIntroDialog;
